import { Component, OnInit } from '@angular/core';
import { debounceTime, distinctUntilChanged, Observable, Subscription, take } from 'rxjs';
import { DATE_FORMAT, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { ICatalog, ICatalogFilter, ICatalogFilterRequest } from '../../models/catalogs.models';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import { AddCatalogSuccess, DeleteCatalogs, DeleteCatalogsSuccess, ExportCatalogs, FetchCatalogs, SetCatalogsFilter, UpdateCatalogSuccess } from '../../store/catalogs.actions';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { encodeFilterObject, parseFilterRequest, refillFilter } from 'src/app/shared/utils/helpers';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { Actions, ofType } from '@ngrx/effects';
import { NavigationService } from 'src/app/shared/router.service';
import { FormControl } from '@angular/forms';
import { CatalogsFilterComponent } from '../catalogs-filter/catalogs-filter.component';
import { SelectionController } from 'src/app/shared/utils/selection-controller';
import { catalogStatusI18nKey, catalogStatusTone, hasCatalogStatus } from '../../utils/catalog-status';

@Component({
  selector: 'app-list-catalogs',
  templateUrl: './list-catalogs.component.html',
  styleUrls: ['./list-catalogs.component.scss']
})
export class ListCatalogsComponent implements OnInit {
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1
  }
  DATE_FORMAT = DATE_FORMAT;
  catalogs$: Observable<ICatalog[]> = this.store.select((state) => state.catalogs.catalogs);
  catalogs: ICatalog[] = [];

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<ICatalog>((catalog) => catalog.id!);

  // Status badge helpers, exposed for the template.
  readonly hasCatalogStatus = hasCatalogStatus;
  readonly catalogStatusTone = catalogStatusTone;
  readonly catalogStatusI18nKey = catalogStatusI18nKey;
  pagination$: Observable<IPagination | null> = this.store.select((state) => state.catalogs.metadata);
  filter: ICatalogFilterRequest | undefined = undefined;
  key: string = '';
  subscriptions: Subscription[] = [];
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);
  baseUrl = environment.BASE_URL_API;
  timestamp = new Date().getTime();
  failedImages = new Set<string>();
  unparsedFilter:ICatalogFilter | null = null;
  module:string = 'catalog';
  activeSortBy: string  = 'id';
  activeSortMode: string = 'desc';
  searchInput = new FormControl('');
  catalogsSelected: ICatalog[] = [];
  constructor(
    private store: Store<AppState>,
    public dialog: MatDialog,
    private router: Router,
    private actions$: Actions,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private navigationService: NavigationService
  ) { 
    this.activatedRoute.queryParams.subscribe(params => {
      const pageNumber = parseInt(params['page'] || '1');
      const pageCount = PAGE_SIZE_OPTIONS.includes(parseInt(params['count'])) ? parseInt(params['count']) : DEFAULT_PAGE_SIZE;
      this.key = params['keyword'] ?? '';
      this.searchInput.setValue(this.key);
      if(params['filter'])
      {
        try {
          this.unparsedFilter = refillFilter(this.module, JSON.parse(decodeURIComponent(params['filter']))) as ICatalogFilter
          if(this.unparsedFilter)
            this.filter = parseFilterRequest(this.module, this.unparsedFilter);
        } catch (error) {
          this.filter == undefined
        }
      }
      this.page = { ...this.page, number: pageNumber, size: pageCount };
    });
  }

  ngOnInit(): void {
    const sub1 = this.store.select((state) => state.catalogs.catalogsFilterState)
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe((filter) => {
        this.unparsedFilter = filter.advanced;
        this.filter = parseFilterRequest(this.module, filter.advanced);
        this.filters.advanced = filter.advanced;
        this.filters.keyword = filter.keyword;
        this.key = filter.keyword;
        this.searchInput.setValue(this.key);
        this.toPage(this.page);
      });

    const sub2 = this.actions$.pipe(ofType(AddCatalogSuccess, UpdateCatalogSuccess)).subscribe(() => {
      this.timestamp = new Date().getTime();
    });

    const sub3 = this.catalogs$.subscribe((catalogs: ICatalog[]) => {
      if (catalogs) {
        this.catalogs = catalogs;
      }
      this.selection.setRows(catalogs);
    });

    this.subscriptions.push(sub1, sub2, sub3);
  }
  filters: any = {}
  search(event: any) {
    const key = event.target.value;
    
    if(this.filters.keyword != key){
      this.filters.keyword = key
      this.store.dispatch(SetCatalogsFilter(this.filters))
      let queryParams:any = {
        page: 1, 
        count: this.page.size,
      }
      if(key != ''){
        queryParams.keyword = key;
      }
      else{
        queryParams.keyword = null;
      }
      this.router.navigate([],{
        relativeTo: this.navigationService.getCurrentRoute(),
        queryParams: {...queryParams},
        queryParamsHandling: 'merge'
      })
    }
  }

  setFilter() {
    const dialogRef = this.dialog.open(CatalogsFilterComponent, {
      minWidth: '650px',
      width: '90vw',
      height: '80vh',
    });
    dialogRef.afterClosed().subscribe((filterResult: any) => {
      if (filterResult) {
        this.page = { ...this.page, number: 1 };
        this.filters.advanced = filterResult;
        this.store.dispatch(SetCatalogsFilter({
          keyword: this.key ?? '',
          advanced: filterResult
        }));
      }
    });
  }

  hasModuleFilters(): boolean {
    if(this.filter)
      return this.filter.filters && this.filter?.filters?.length > 0;
    return false;
  }

  clearFilter() {
    this.page = { ...this.page, number: 1 };
    let queryParams: any = { page: this.page.number, count: this.page.size, filter: null };
    if (this.key && this.key !== '') {
      queryParams.keyword = this.key;
    }
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    this.filters.advanced = refillFilter(this.module, null);
    this.unparsedFilter = null;
    this.filter = parseFilterRequest(this.module, this.filters.advanced);
    this.store.dispatch(SetCatalogsFilter({
      keyword: this.key ?? '',
      advanced: this.filters.advanced
    }));
  }
  

  addCatalog(type: string) {
    if(type == 'agent') {
      this.router.navigate(['/agents/new-catalog'], {
        queryParamsHandling: 'merge',
      })
    } else {
      this.router.navigate(['new-catalog'], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
      })
    }
  }

  getImage(image: string): string {
    return this.baseUrl + image + '?thumbnail=1&timestamp=' + this.timestamp;
  }

  onImageError(imageUrl: string): void {
    this.failedImages.add(imageUrl); // Mark this image as failed
  }

  expired(catalog: ICatalog) {
    if (!catalog.end_date) return false
    const endDate = new Date(catalog.end_date!);
    endDate.setHours(23, 59, 59, 999);
    return endDate < new Date();
  }

  catalogTypeIcon(type: string): string {
    switch(type){
      case 'agent':
        return 'assets/icones/briefcase.svg';
      case 'client':
        return 'assets/icones/companies.svg';
      default:
        return '';
    }
  }

  sortBy(key: string, mode: 'asc' | 'desc') {
    if(key == this.activeSortBy && mode == this.activeSortMode) return;

    this.activeSortBy = key;
    this.activeSortMode = mode;
    this.toPage(this.page);
  }

  toPage(page: IPageCount) {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(FetchCatalogs({
        page: this.page,
        filter: this.filter,
      key: this.key,
      sort_by: this.activeSortBy,
      sort_mode: this.activeSortMode
    }));
    let filter:any = {}
    if(this.key && this.key != ''){
      filter.keyword = this.key;
    }
    filter.filter = encodeFilterObject(this.unparsedFilter);
    let queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate([], {
      queryParams: queryParams
    });
  }

  editCatalogs(catalog: ICatalog) {
    if(catalog.type == 'client') {
      // let filter:any = {}
      // if(this.key && this.key != ''){
      //   filter.keyword = this.key;
      // }
      // filter.filter = encodeFilterObject(this.unparsedFilter);
      // let queryParams = { page: this.page.number, count: this.page.size, ...filter };
      // this.router.navigate(['catalogs/', catalog.id],{
      //   queryParams: queryParams,
      // });
      this.router.navigate(['edit-catalog', catalog.id], {
        relativeTo: this.activatedRoute,
        queryParamsHandling: 'merge',
      })
    }
    if(catalog.type == 'agent') {
      this.router.navigate(['catalogs/agents-catalog/edit-catalog', catalog.id])
    }
  }

  deleteSelectedCatalogs() {
    // Read the ids before anything can clear them: the success handler empties
    // the selection, and the dispatch below must not depend on when it runs.
    const pricelistIds = this.selection.ids;

    const deleteSuccessSub = this.actions$.pipe(
      ofType(DeleteCatalogsSuccess),
      take(1)
    ).subscribe(() => {
      this.store.dispatch(FetchCatalogs({
        page: this.page,
        filter: this.filter,
        key: this.key,
        sort_by: this.activeSortBy,
        sort_mode: this.activeSortMode
      }));
      this.selection.clear();
      deleteSuccessSub.unsubscribe();
    });
    
    this.store.dispatch(DeleteCatalogs({ pricelist_ids: pricelistIds }));
  }

    deleteConfirmation() {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: 'assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.deleteSelectedCatalogs();
      }
    });
  }

  downloadCatalogs() {
    this.store.dispatch(ExportCatalogs(
      { filter: this.filter!, key: this.key! }
    ));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub => sub.unsubscribe()))
  }

}
